import type { CallHistoryMethodAction } from 'connected-react-router';
import type { Action, Reducer } from 'redux';
import { IAnalyticsAction, IApplicationState, IAppThunkAction } from '.';
import Invoices from '../service/invoices';
import _ from 'lodash';
import { Routes, navigateTo } from '../helpers/routes';
import { ICreateInvoiceRequest, IUpdateInvoiceRequest, PaymentStatusType } from '../interfaces/IInvoice';
import { GoogleTagManagerEvents } from '../helpers/googleTagManager';
import { ICharge } from '../interfaces/ICharge';
import { CLEAR_BOOKING, IClearBookingAction } from './Booking';
import { IDiscountCode } from '../interfaces/IDiscountCode';
import { appInsights } from '../Logging/appInsights';

export interface IInvoiceState {
    loading: boolean;
    invoiceId?: number;
    siteId?: number;
    paymentStatusTypeId?: PaymentStatusType;
    charge?: ICharge;
    appliedCodes: Array<IDiscountCode>;
}

export const LOADING_INVOICE: 'LOADING_INVOICE' = 'LOADING_INVOICE';
export const LOADED_INVOICE: 'LOADED_INVOICE' = 'LOADED_INVOICE';
export const STATUS_UPDATE_INVOICE: 'STATUS_UPDATE_INVOICE' = 'STATUS_UPDATE_INVOICE';
export const CONFIRM_INVOICE_SUCCESS: 'CONFIRM_INVOICE_SUCCESS' = 'CONFIRM_INVOICE_SUCCESS';
export const CLEAR_INVOICE: 'CLEAR_INVOICE' = 'CLEAR_INVOICE';
export const INVOICE_CODE_LOADING: 'INVOICE_CODE_LOADING' = 'INVOICE_CODE_LOADING';
export const INVOICE_CODE_SUCCESS: 'INVOICE_CODE_SUCCESS' = 'INVOICE_CODE_SUCCESS';
export const INVOICE_CODE_FAILURE: 'INVOICE_CODE_FAILURE' = 'INVOICE_CODE_FAILURE';
export const INVOICE_CODE_REMOVED: 'INVOICE_CODE_REMOVED' = 'INVOICE_CODE_REMOVED';

export interface IClearInvoiceAction extends Action {
    type: typeof CLEAR_INVOICE;
}

export interface ILoadingInvoiceAction extends Action {
    type: typeof LOADING_INVOICE;
}

export interface ILoadedInvoiceAction extends Action {
    type: typeof LOADED_INVOICE;
    invoiceId?: number;
    siteId?: number;
    paymentStatusTypeId?: number;
    charge?: ICharge;
    appliedCodes: Array<IDiscountCode>;
}

export interface IInvoiceStatusUpdateAction extends Action {
    type: typeof STATUS_UPDATE_INVOICE;
    paymentStatusTypeId?: number;
}

export interface IConfirmInvoiceSuccessAction extends IAnalyticsAction {
    type: typeof CONFIRM_INVOICE_SUCCESS;
}

export interface IInvoiceCodeLoadingAction extends IAnalyticsAction {
    type: typeof INVOICE_CODE_LOADING;
}

export interface IInvoiceCodeSuccessAction extends Action {
    type: typeof INVOICE_CODE_SUCCESS;
    charge?: ICharge;
    paymentStatusTypeId?: number;
    appliedCodes: Array<IDiscountCode>;
}

export interface IInvoiceCodeFailureAction extends Action {
    type: typeof INVOICE_CODE_FAILURE;
}

export interface IInvoiceCodeRemovedAction extends Action {
    type: typeof INVOICE_CODE_REMOVED;
    charge?: ICharge;
    paymentStatusTypeId?: number;
    appliedCodes: Array<IDiscountCode>;
}

export type KnownInvoiceActions =
    ILoadingInvoiceAction
    | IClearInvoiceAction
    | ILoadedInvoiceAction
    | IInvoiceStatusUpdateAction
    | IConfirmInvoiceSuccessAction
    | CallHistoryMethodAction
    | IClearBookingAction
    | IInvoiceCodeLoadingAction
    | IInvoiceCodeSuccessAction
    | IInvoiceCodeFailureAction
    | IInvoiceCodeRemovedAction;

let inFlightInvoice: AbortController | null = null;

export const actionCreators = {
    clearInvoice: (): IAppThunkAction<KnownInvoiceActions> => (dispatch) => {
        dispatch({ type: CLEAR_INVOICE } as IClearInvoiceAction);
    },
    
    createInvoice:
        (
            siteId: number,
            createInvoiceRequest: ICreateInvoiceRequest
        ): IAppThunkAction<KnownInvoiceActions> =>
        (dispatch: any, _getState: any) => {
            if (inFlightInvoice) inFlightInvoice.abort('createInvoice called with newer state');
            const controller = new AbortController();
            inFlightInvoice = controller;

            dispatch({ type: LOADING_INVOICE } as ILoadingInvoiceAction);

            Invoices.CreateInvoice(siteId, createInvoiceRequest, controller.signal)
                .then((response) => {
                    dispatch({
                        type: LOADED_INVOICE,
                        charge: response.charge,
                        invoiceId: response.invoiceId,
                        siteId: response.siteId,
                        paymentStatusTypeId: response.paymentStatusTypeId
                    } as ILoadedInvoiceAction);
                })
                .catch((error: Error) => {
                    if (error.name === 'AbortError') return;
                    console.error('Error creating invoice', error);
                })
                .finally(() => {
                    if (inFlightInvoice === controller) inFlightInvoice = null;
                });
        },

    updateInvoice:
        (
            siteId: number,
            invoiceId: number,
            updateInvoiceRequest: IUpdateInvoiceRequest
        ): IAppThunkAction<KnownInvoiceActions> =>
        (dispatch: any, _getState: any) => {
            if (inFlightInvoice) inFlightInvoice.abort('updateInvoice called with newer state');
            const controller = new AbortController();
            inFlightInvoice = controller;

            dispatch({
                type: LOADING_INVOICE
            } as ILoadingInvoiceAction);

            Invoices.UpdateInvoice(siteId, invoiceId, updateInvoiceRequest, controller.signal)
                .then((response) => {
                    dispatch({
                        type: LOADED_INVOICE,
                        charge: response.charge,
                        invoiceId: response.invoiceId,
                        siteId: response.siteId,
                        paymentStatusTypeId: response.paymentStatusTypeId,
                        appliedCodes: response.discountCodes
                    } as ILoadedInvoiceAction);
                })
                .catch((error: Error) => {
                    if (error.name === 'AbortError') return;
                    console.error('Error updating invoice', error);
                })
                .finally(() => {
                    if (inFlightInvoice === controller) inFlightInvoice = null;
                });
        },
        
    applyCode:
        (
            siteId: number,
            invoiceId: number,
            code: string
        ): IAppThunkAction<KnownInvoiceActions> =>
        (dispatch: any, _getState: any) => {
            dispatch({
                type: INVOICE_CODE_LOADING
            } as IInvoiceCodeLoadingAction);

            Invoices.ApplyCode(siteId, invoiceId, code)
                .then((response) => {
                    dispatch({
                        type: INVOICE_CODE_SUCCESS,
                        charge: response.charge,
                        paymentStatusTypeId: response.paymentStatusTypeId,
                        appliedCodes: response.discountCodes
                    } as IInvoiceCodeSuccessAction);
                })
                .catch((error) => {
                    console.error('Error applying code to invoice', error);
                    dispatch({
                        type: INVOICE_CODE_FAILURE
                    } as IInvoiceCodeFailureAction);
                });
        },
        
    removeCode:
        (code: string): IAppThunkAction<KnownInvoiceActions> =>
        (dispatch: any, getState: any) => {
            const appState: IApplicationState = getState();

            if(appState.invoice.invoiceId) {
                const siteId = appState.invoice.siteId!;
                const invoiceId = appState.invoice.invoiceId;

                dispatch({
                    type: INVOICE_CODE_LOADING
                } as IInvoiceCodeLoadingAction);

                Invoices.RemoveCode(siteId, invoiceId, code)
                    .then((response) => {
                        dispatch({
                            type: INVOICE_CODE_REMOVED,
                            charge: response.charge,
                            paymentStatusTypeId: response.paymentStatusTypeId,
                            appliedCodes: response.discountCodes
                        } as IInvoiceCodeRemovedAction);
                    })
                    .catch((error) => {
                        console.error('Error applying code to invoice', error);
                        dispatch({
                            type: INVOICE_CODE_FAILURE
                        } as IInvoiceCodeFailureAction);
                    });
            }
        },
        
    checkInvoiceStatus:
        (siteId: number, invoiceId: number): IAppThunkAction<KnownInvoiceActions> =>
        (dispatch, getState) => {
            Invoices.GetInvoice(siteId, invoiceId)
                .then((response) => {
                    const appState = getState();
                    if (appState.invoice.paymentStatusTypeId !== response.paymentStatusTypeId) {
                        dispatch({
                            type: STATUS_UPDATE_INVOICE,
                            paymentStatusTypeId: response.paymentStatusTypeId
                        } as IInvoiceStatusUpdateAction);
                    }
                })
                .catch((exception) => {
                    console.error('Error checking invoice status', exception);
                });
        },

    confirmInvoice:
        (
            paymentMethodTypeId: number
        ): IAppThunkAction<any> =>
        (dispatch, getState) => {
            const appState: IApplicationState = getState();

            if (appState && appState.invoice.invoiceId && appState.invoice.invoiceId > 0 && appState.invoice.charge) {
                const siteId = appState.invoice.siteId!;
                const invoiceId = appState.invoice.invoiceId;
                const charge = appState.invoice.charge;

                //move to processing page before starting
                dispatch(navigateTo(Routes.Processing));

                Invoices.ConfirmInvoice(
                    siteId,
                    invoiceId,
                    {
                        amount: charge.total,
                        currencyCode: charge.currencyCode,
                        dateCreated: new Date().toISOString(),
                        paymentMethodTypeId: paymentMethodTypeId,
                        siteId: siteId,
                        tax: charge?.tax ?? 0,
                        emailAddress: '', //not needed for invoice confirmation
                        fullName: undefined, //not needed for invoice confirmation
                        paymentMethodReference: '' //not needed for invoice confirmation
                    }
                )
                    .then((_data) => {
                        dispatch(actionCreators.confirmInvoiceSuccess());
                    })
                    .catch((error: Error) => {
                        //dispatch({ type: CONFIRM_INVOICE_FAILED });
                         appInsights?.trackException(
                             { error: new Error(`Error confirming invoice`), severityLevel: 2},
                             { exception: error, sourceFile: "Invoice.ts", product: "invoice" });
                    });
            }
        },

    confirmInvoiceSuccess: (): IAppThunkAction<KnownInvoiceActions> => (dispatch, getState) => {
        const appState: IApplicationState = getState();
        const invoiceId = appState.invoice.invoiceId;
        const charge = appState.invoice.charge;

        if (charge && invoiceId) {
            dispatch({
                type: CONFIRM_INVOICE_SUCCESS,
                googleTagManagerEvent: GoogleTagManagerEvents.ConfirmInvoice(invoiceId),
            });

            dispatch(navigateTo(Routes.Complete, appState.sites.selectedSite?.urlKey));
        }
    }
        
};

const unloadedState: IInvoiceState = {
    loading: false,
    appliedCodes: []
};

export const reducer: Reducer<IInvoiceState> = (
    state: IInvoiceState | undefined,
    incomingAction: Action
): IInvoiceState => {
    if (state === undefined) return unloadedState;

    const action: KnownInvoiceActions = incomingAction as KnownInvoiceActions;
    switch (action.type) {
        case CLEAR_INVOICE:
        case CLEAR_BOOKING:
            return {
                ...unloadedState
            };
        case LOADING_INVOICE:
            return {
                ...state,
                loading: true
            };
        case LOADED_INVOICE:
            return {
                ...state,
                loading: false,
                invoiceId: action.invoiceId,
                siteId: action.siteId,
                paymentStatusTypeId: action.paymentStatusTypeId,
                charge: action.charge,
                appliedCodes: action.appliedCodes ?? []
            };
        case STATUS_UPDATE_INVOICE:
            return {
                ...state,
                paymentStatusTypeId: action.paymentStatusTypeId
            };
        case CONFIRM_INVOICE_SUCCESS:
            return {
                ...state,
                paymentStatusTypeId: PaymentStatusType.Completed
            };
        case INVOICE_CODE_LOADING:
            return {
                ...state,
                loading: true
            };
        case INVOICE_CODE_SUCCESS:
        case INVOICE_CODE_REMOVED:
            return {
                ...state,
                loading: false,
                charge: action.charge,
                paymentStatusTypeId: action.paymentStatusTypeId,
                appliedCodes: action.appliedCodes ?? []
            };
        case INVOICE_CODE_FAILURE:
            return {
                ...state,
                loading: false
            };
        default:
            return state;
    }
};
